<template>
  <RequiresPermission
    permission="bulk_unpair_terminals"
    fallback-route-name="merchantIndex"
  >
    <BaseCard class="flex flex-col overflow-y-auto">
      <BaseInnerLoading v-if="isLoading" />
      <div ref="scrollMarkerRef"></div>
      <p>
        Here you can easily unpair a list of terminal
        devices. Use the following steps to do so:
      </p>
      <ol>
        <li>
          1. Type or copy and paste a list of serial numbers
          separated by new lines into the text box below.
          Then press submit.
        </li>
        <li>
          2. Review the serial numbers that will be
          unpaired, have already been unpaired or haven't
          been found on our system.
        </li>
        <li>
          3. Click confirm to unpair the listed serial
          numbers.
        </li>
      </ol>

      <div class="mt-3 p-2">
        <div v-if="step === 'input'" class="flex flex-col">
          <textarea
            v-model="serialsText"
            rows="15"
            class="mt-3 block w-full resize-none rounded-md border border-background-fg/6 p-2 text-sm"
          >
          </textarea>
          <ButtonPrimary
            class="mt-2 self-end"
            @click="checkUnpair"
            >Submit</ButtonPrimary
          >
        </div>

        <div
          v-if="step === 'confirm' || step === 'finished'"
          class="flex flex-col"
        >
          <div v-if="step === 'confirm'" class="mt-1">
            <p class="text-lg font-semibold"
              >Terminals To Unpair</p
            >

            <BaseTable
              :rows="terminalsToUnpair"
              :columns="columnDefinitions"
            />
          </div>
          <div
            v-if="alreadyUnpairedTerminals.length > 0"
            class="mt-1"
          >
            <p class="text-lg font-semibold"
              >{{
                step === "confirm"
                  ? "Terminals already unpaired"
                  : "Termianls unpaired"
              }}
            </p>
            <BaseTable
              :rows="alreadyUnpairedTerminals"
              :columns="columnDefinitions.slice(0, 1)"
            />
          </div>
          <div
            v-if="missingSerialNumbers.length > 0"
            class="mt-1 font-semibold"
          >
            <p class="text-lg">Terminals not found</p>
            <BaseTable
              :rows="missingSerialNumbers"
              :columns="columnDefinitions.slice(0, 1)"
            />
          </div>
          <ButtonPrimary
            v-if="step !== 'finished'"
            class="mt-2 self-end"
            @click="confirmUnpair"
            >Confirm</ButtonPrimary
          >
          <ButtonPrimary
            v-if="step === 'finished'"
            class="mt-2 self-end"
            @click="reset"
            >Reset</ButtonPrimary
          >
        </div>
      </div>
    </BaseCard>
  </RequiresPermission>
</template>

<script setup lang="ts">
import type { BulkUnpairTerminalsResponse } from "@/services/terminals/bulkUnpairTerminals"
import type { TableColumn } from "@/components/BaseTable.vue"

const isLoading = ref(false)

const scrollMarkerRef = useTemplateRef("scrollMarkerRef")
const scrollToTop = () => {
  if (scrollMarkerRef.value)
    scrollMarkerRef.value.scrollIntoView()
}

const serialsText = ref("")
const serialsSplit = ref<string[]>([])
const step = ref<"input" | "confirm" | "finished">("input")

const unpairResponse = ref<BulkUnpairTerminalsResponse>()
const terminalsToUnpair = computed<
  BulkUnpairTerminalsResponse["terminals"]
>(
  () =>
    unpairResponse.value?.terminals.filter(
      (v) => v.clientName !== null
    ) ?? []
)
const alreadyUnpairedTerminals = computed<
  BulkUnpairTerminalsResponse["terminals"]
>(
  () =>
    unpairResponse.value?.terminals.filter(
      (v) => v.clientName === null
    ) ?? []
)
const missingSerialNumbers = computed<
  BulkUnpairTerminalsResponse["terminals"]
>(
  () =>
    unpairResponse.value?.missingSerials.map((v) => ({
      serialNumber: v,
      clientName: null,
      clientMid: null,
    })) ?? []
)

const checkUnpair = async () => {
  isLoading.value = true
  serialsSplit.value = [
    ...new Set(
      serialsText.value
        .split(/\r?\n/)
        .filter((s) => s !== "")
    ),
  ].map((v) => v.trim())

  if (serialsSplit.value.length < 1) return
  try {
    const response = await bulkUnpairTerminals({
      serialNumbers: serialsSplit.value,
    })
    unpairResponse.value = response

    step.value = "confirm"
    scrollToTop()
  } catch {
    toast.error(
      "Something went wrong, please refresh and try again."
    )
  } finally {
    isLoading.value = false
  }
}

const confirmUnpair = async () => {
  isLoading.value = true

  try {
    const response = await bulkUnpairTerminals({
      serialNumbers: serialsSplit.value,
      confirmed: true,
    })
    unpairResponse.value = response
    step.value = "finished"
    scrollToTop()
  } catch {
    toast.error(
      "Something went wrong, please refresh and try again."
    )
  } finally {
    isLoading.value = false
  }
}

const reset = () => {
  serialsText.value = ""
  serialsSplit.value = []
  step.value = "input"
  unpairResponse.value = undefined
}

const columnDefinitions: Array<
  TableColumn<
    {
      serialNumber: string
      clientName: string | null
      clientMid: string | null
    },
    "serialNumber" | "clientName" | "clientMid"
  >
> = [
  {
    id: "serialNumber",
    header: "Serial Number",
    getCellValue: (row) => row.serialNumber,
  },
  {
    id: "clientName",
    header: "Merchant Name",
    getCellValue: (row) => row.clientName,
  },
  {
    id: "clientMid",
    header: "MID",
    getCellValue: (row) => row.clientMid,
  },
]
</script>
